import { useState } from 'react';
import clsx from 'clsx';
import { setBundelkorting } from '../../lib/bundelkorting';

interface BundelkortingToggleProps {
  /** Lead-Id (offerte) of Werk_Order__c-Id (order). */
  recordId: string;
  /** Huidige stand: true = bundelkorting uitgesloten. */
  geenBundelkorting: boolean;
  canEdit: boolean;
  /** Wordt aangeroepen na een geslaagde wijziging (herlaadt de detail). */
  onSaved: () => void;
}

/**
 * Compacte pill om de automatische bundelkorting per offerte/order aan of uit te
 * zetten (B2B-opt-out). Eén klik schakelt; Salesforce herprijst direct. Bewuste
 * KORTING:-/campagnekortingen blijven ongemoeid.
 */
export function BundelkortingToggle({ recordId, geenBundelkorting, canEdit, onSaved }: BundelkortingToggleProps) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const uit = geenBundelkorting === true;

  async function toggle() {
    if (!canEdit || pending) return;
    setPending(true);
    setError(null);
    try {
      await setBundelkorting(recordId, !uit);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Wijzigen mislukt');
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="relative inline-flex flex-col">
      <button
        type="button"
        onClick={toggle}
        disabled={!canEdit || pending}
        title={
          uit
            ? 'Automatische bundelkorting staat UIT voor deze offerte/order. Klik om weer aan te zetten.'
            : 'Automatische bundelkorting staat aan. Klik om uit te zetten (bijv. B2B).'
        }
        className={clsx(
          'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
          uit
            ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'
            : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50',
          (!canEdit || pending) && 'cursor-not-allowed opacity-60',
        )}
      >
        <span
          className={clsx(
            'h-1.5 w-1.5 rounded-full',
            uit ? 'bg-amber-500' : 'bg-emerald-500',
          )}
        />
        {pending ? 'Bezig…' : uit ? 'Bundelkorting uit' : 'Bundelkorting aan'}
      </button>
      {error && <span className="mt-1 text-[10px] text-red-600">{error}</span>}
    </div>
  );
}
